/**
 * Cron reading for scheduled validation rules — shared by the wizard (which
 * validates what the author types) and the detail page (which shows what a saved
 * rule will do next).
 *
 * Mirrors `scheduleCron.ts` on the backend. The two parse independently with
 * their own copy of `cron-parser`, the same arrangement `@erp/workflows` uses:
 * the library stays out of the shared packages, and only the *decisions* are
 * expected to agree. The server remains the authority — everything here is the
 * courtesy copy that stops an author before a 400.
 */

import { CronExpressionParser } from 'cron-parser';

/**
 * Rules carry no per-rule timezone, so every cron is read in UTC — matching
 * `VALIDATION_SCHEDULE_TIMEZONE` on the server.
 */
export const SCHEDULE_TIMEZONE = 'UTC';

/** Longest cron expression worth trying to parse. */
export const MAX_CRON_LENGTH = 120;

/**
 * Jenkins-style `H` (hash) fields are rejected, mirroring the backend:
 * `cron-parser` resolves `H` with a random seed per parse, so an `H` schedule
 * is nondeterministic — the previewed next run would never match execution.
 * Word-bounded so day/month names that contain an H (THU, MARCH) still parse.
 */
const HASH_FIELD = /\b[hH]\b/;

/**
 * The example cron shown as the input's placeholder — every day at 06:00 UTC.
 *
 * A constant rather than a JSX literal because it is an expression in a machine
 * syntax, not prose: `i18next/no-literal-string` would flag it, and translating
 * it would be wrong. The surrounding hint text, which *is* prose, is translated
 * normally.
 */
export const CRON_EXAMPLE = '0 6 * * *';

/**
 * Whether `cron-parser` accepts an expression.
 *
 * Blank is rejected explicitly: `parse('')` *succeeds* and means every minute,
 * which is never what an empty field intends.
 */
export function isValidCronExpression(expression: string): boolean {
  if (!expression.trim()) return false;
  if (expression.length > MAX_CRON_LENGTH) return false;
  if (HASH_FIELD.test(expression)) return false;
  try {
    CronExpressionParser.parse(expression, { tz: SCHEDULE_TIMEZONE });
    return true;
  } catch {
    return false;
  }
}

/**
 * The next time a cron will fire, or null when it cannot be parsed. Formatting
 * is the caller's business.
 */
export function nextCronRun(expression: string): Date | null {
  if (!isValidCronExpression(expression)) return null;
  try {
    return CronExpressionParser.parse(expression, { tz: SCHEDULE_TIMEZONE })
      .next()
      .toDate();
  } catch {
    return null;
  }
}
